<script setup lang="ts">
import type { SiteResourceDetailed, SitePullResource } from "@carnet/api/backoffice";

const props = defineProps<{
  detail: SiteResourceDetailed | SitePullResource;
}>();

const { t } = useI18n();
const { textFields, externalUrl } = useAppSiteDetailView(() => props.detail);

const FEEDBACK_URL = "https://w1g9ws629jb.typeform.com/to/PzbwqBjV";
</script>

<template>
  <AppSection class="bg-white overflow-hidden" :ui="{ base: 'py-20' }">
    <div v-if="detail.description || externalUrl || detail.images.length" class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-10 md:mb-20">
      <h2 class="text-4xl font-semibold">{{ t("site.description") }}</h2>

      <div class="flex flex-col gap-10">
        <div v-if="detail.description || externalUrl" class="flex flex-col gap-2">
          <p class="text-lg font-semibold uppercase text-blue-700">{{ t("site.descriptionLabel") }}</p>
          <p v-if="detail.description" class="text-base whitespace-pre-line">{{ detail.description }}</p>
          <div v-if="externalUrl" class="mt-2">
            <UButton variant="link" :to="externalUrl" target="_blank" rel="noopener noreferrer" trailing-icon="icon:external">
              {{ t("ui.knowMore") }}
            </UButton>
          </div>
        </div>

        <AppCarouselWide v-if="detail.images.length" :items="detail.images" />

        <div v-if="textFields.length" class="flex flex-col gap-10">
          <div v-for="field in textFields" :key="field.key" class="flex flex-col gap-2">
            <p class="text-lg font-semibold uppercase text-blue-700">{{ field.label }}</p>
            <p v-if="field.value" class="text-base whitespace-pre-line">{{ field.value }}</p>
            <UButton
              v-if="field.link"
              variant="link"
              :to="field.link"
              target="_blank"
              rel="noopener noreferrer"
              trailing-icon="icon:external"
              class="mt-2 self-start"
            >
              {{ field.linkLabel }}
            </UButton>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-elevated rounded-lg p-4 md:p-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <p class="text-lg text-blue-700">{{ t("site.feedback.prompt") }}</p>
      <UButton variant="link" :to="FEEDBACK_URL" target="_blank" rel="noopener noreferrer" trailing-icon="icon:external">
        {{ t("site.feedback.cta") }}
      </UButton>
    </div>
  </AppSection>
</template>
